import { HttpClient, HttpErrorResponse } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable, Subject } from 'rxjs';
import { catchError, map } from 'rxjs/operators';
import { Cacheable } from 'ts-cacheable';
import DashboardStat from '../../models/DashboardStat';
import MaintenanceInvoiceFile from '../../models/MaintenanceInvoiceFile';
import Statement from '../../models/Statement';
import { StatementResponse } from '../../models/StatementResponse';
import { BaseService } from '../base.service';

export const statementsCacheBuster$ = new Subject<void>();

export interface StatementData {
  files: MaintenanceInvoiceFile[];
  statements: Statement[];
  hideGraph: boolean;
  hideStatements: boolean;
}
@Injectable({
  providedIn: 'root',
})
export class StatementService extends BaseService {
  constructor(protected http: HttpClient) {
    super(http);
  }

  @Cacheable({
    maxAge: 1000 * 60 * 5,
    cacheBusterObserver: statementsCacheBuster$,
  })
  fetchData(areaId = null): Observable<StatementData> {
    const url = areaId
      ? `owner/files/statement/${areaId}`
      : `owner/files/statement`;
    return this.callGet(url).pipe(
      map((response: StatementResponse) => {
        if (response && response.data) {
          return {
            files: response.data.files.map(
              MaintenanceInvoiceFile.parseFromData
            ),
            hideGraph:
              !response.data.statements || !response.data.statements.length, // if our API returns no graph data, it means we don't have permissions, it is possible to get empty collection
            hideStatements:
              !response.data.statements || !response.data.statements.length, // same as above
            statements: response.data.statements
              ? response.data.statements.map(Statement.parseFromData)
              : [],
          };
        }
        return {
          files: [],
          statements: [],
          hideGraph: true,
          hideStatements: true,
        };
      }),
      catchError((response: HttpErrorResponse) => {
        return this.getResponseMessage(
          response,
          'Unable to fetch statement data'
        );
      })
    );
  }

  getDownloadUrl(id: number): Observable<string> {
    return this.callGet(`owner/download/${id}`).pipe(
      catchError((response: HttpErrorResponse) => {
        return this.getResponseMessage(response, 'Unable to get file');
      })
    );
  }

  getDownloadStatementUrl(statement: any): Observable<string> {
    return this.callGet(
      `owner/statement/${statement.areaId}/${statement.month}/${statement.year}/${statement.statementType}`
    ).pipe(
      catchError((response: HttpErrorResponse) => {
        return this.getResponseMessage(response, 'Unable to get statement');
      })
    );
  }

  fetchDashboardStats(): Observable<any> {
    return this.callGet('owner/dashboard-stats').pipe(
      map((response: any) => {
        if (response && response.data) {
          return response.data.map(DashboardStat.parseFromData);
        }
        return [];
      }),
      catchError((response: HttpErrorResponse) => {
        return this.getResponseMessage(
          response,
          'Unable to fetch dashboard stats'
        );
      })
    );
  }
}
